'use client';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tooltip } from 'src/components/PulseUI/Tooltip';
import useTableFilterSelections, { extractBaseId } from 'src/hooks/filters/useTableFilterSelections';
import { LOCATION } from 'src/hooks/filters/constants';
import { claimsMetricWord } from 'src/utils/encounterLabels';
import { useGetProviderCohortsQuery } from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';

interface SearchVolumeCellProps {
  row: {
    name: string;
    patients?: Record<string, number>;
    claims?: Record<string, number>;
    encounters?: Record<string, number>;
  };
  searchId: string;
  location: string;
  profileName?: string;
}

const generateTooltipMessage = (params: {
  profileName?: string;
  searchName?: string;
  rowName: string;
  patients: string | number;
  claims: string | number;
  claimsWord: string;
  isClaims: boolean;
  isSending: boolean;
  isInbound: boolean;
  isReceiving: boolean;
  isOutbound: boolean;
  isCodeVolume: boolean;
}) => {
  const {
    profileName,
    searchName,
    rowName,
    patients,
    claims,
    claimsWord,
    isClaims,
    isSending,
    isInbound,
    isReceiving,
    isOutbound,
    isCodeVolume,
  } = params;

  if (isSending) {
    return isClaims
      ? profileName
        ? isInbound
          ? `${rowName} has referred ${claims} ${claimsWord} into ${profileName} for ${searchName}`
          : `${profileName} has referred ${claims} ${claimsWord} to ${rowName} for ${searchName}`
        : `${rowName} has referred ${claims} ${claimsWord} for ${searchName}`
      : profileName
      ? `${rowName} has referred ${patients} patients ${
          isInbound ? 'into' : 'to'
        } ${profileName} for ${searchName}`
      : `${rowName} has referred ${patients} patients for ${searchName}`;
  }

  if (isReceiving && isOutbound) {
    return `${profileName} has referred ${patients} patients out to ${rowName} for ${searchName}`;
  }

  if ((isInbound || isOutbound) && isCodeVolume) {
    return isClaims
      ? `${rowName} has a total ${claimsWord} volume of ${claims} for ${searchName}`
      : `${rowName} has a total patients volume of ${patients} for ${searchName}`;
  }

  return '';
};

const SearchVolumeCell: React.FC<SearchVolumeCellProps> = ({
  row,
  searchId,
  location,
  profileName,
}) => {
  const baseSearchId = extractBaseId(searchId);
  const { showEncountersTotals } = useFlags();

  const { data: providerCohorts } = useGetProviderCohortsQuery();
  const { data: filterSets } = useGetFilterSetsQuery({});

  const cohort = [
    ...(providerCohorts?.results || []),
    ...(filterSets?.results || []),
  ]?.find((cohort) => cohort?.id === Number(baseSearchId));

  const { apiVolumeFilterFormat } = useTableFilterSelections(location);

  let columnSettings = apiVolumeFilterFormat?.[searchId];

  if (!columnSettings && Object.keys(apiVolumeFilterFormat).length > 0) {
    const firstAvailableKey = Object.keys(apiVolumeFilterFormat)[0];
    columnSettings = apiVolumeFilterFormat[firstAvailableKey];
  }

  const columnType = columnSettings?.type || 'claims';
  // For encounters (cohort) columns, source the claims value from encounters instead
  // and say "encounters" in the tooltip. Gated by the flag; only the claims metric.
  const isEncounters = !!showEncountersTotals && !!columnSettings?.cohort_id;
  const claimsWord = claimsMetricWord(
    !!columnSettings?.cohort_id,
    !!showEncountersTotals
  );
  const volumeData =
    columnType === 'patients'
      ? row.patients
      : isEncounters
        ? row.encounters
        : row.claims;
  const volume = volumeData?.[searchId];

  let tooltipContent = '';

  if (volume) {
    const searchName = cohort?.label || `Search ${searchId}`;
    const formattedVolume =
      typeof volume === 'number' ? volume.toLocaleString() : volume;

    const isClaims =
      columnSettings?.type === 'claims' || columnType === 'claims';
    const isSending = columnSettings?.mode === 'sending';
    const isReceiving = columnSettings?.mode === 'receiving';
    const isCodeVolume = columnSettings?.mode === 'treating';
    const isInbound = location === LOCATION.INBOUND_REFERRALS;
    const isOutbound = location === LOCATION.OUTBOUND_REFERRALS;

    const message = generateTooltipMessage({
      profileName,
      searchName,
      rowName: row.name,
      patients: formattedVolume,
      claims: formattedVolume,
      claimsWord,
      isClaims,
      isSending,
      isInbound,
      isReceiving,
      isOutbound,
      isCodeVolume,
    });

    let fallbackMessage = '';
    if (isSending) {
      fallbackMessage = isClaims
        ? `${row.name} has referred ${formattedVolume} ${claimsWord} ${
            profileName ? `to ${profileName}` : ''
          } for ${searchName}`
        : `${row.name} has referred ${formattedVolume} patients ${
            profileName ? `${isInbound ? 'into' : 'to'} ${profileName}` : ''
          } for ${searchName}`;
    } else if (isReceiving && isOutbound) {
      fallbackMessage = `${
        profileName || 'Provider'
      } has referred ${formattedVolume} patients out to ${
        row.name
      } for ${searchName}`;
    } else if (isCodeVolume) {
      fallbackMessage = isClaims
        ? `${row.name} has treated ${formattedVolume} ${claimsWord} for ${searchName}`
        : `${row.name} has treated ${formattedVolume} patients for ${searchName}`;
    } else {
      fallbackMessage = `${row.name} has ${formattedVolume} ${
        isClaims ? claimsWord : 'patients'
      } for ${searchName}`;
    }

    tooltipContent = message || fallbackMessage;
  }

  if (!volume) {
    return (
      <div className="flex items-center justify-end px-1 w-full h-full text-neutral-400 font-['Inter'] font-normal text-base">
        <span>-</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end px-1 w-full h-full text-primary font-['Inter'] font-normal text-base min-w-0">
      {tooltipContent ? (
        <Tooltip
          content={tooltipContent}
          slotProps={{
            containerClassName:
              'bg-white !text-black border border-neutral-200 font-medium text-sm !w-32 !max-w-32 shadow-xl rounded-lg p-3 leading-relaxed',
          }}
        >
          <span className="cursor-pointer">{volume}</span>
        </Tooltip>
      ) : (
        <span>{volume}</span>
      )}
    </div>
  );
};

export default SearchVolumeCell;
